import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Product, Review } from '../types/types';
import { ROUTES, PRODUCT_CATEGORIES } from '../data/constants';
import dataService from '../data/dataService';
import PetIcon from '../components/icons/PetIcons';
import AddToCartButton from '../cart/AddToCartButton';
import { useAuth } from '../auth/useAuth';
import '../styles/ProductDetails.css';

const ProductDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [product, setProduct] = useState<Product | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const { isAuthenticated, user, login } = useAuth();

  // Review form state
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    const loadProduct = async () => {
      if (!id) return;
      setLoading(true);
      const data = await dataService.getProduct(Number(id));
      if (data) {
        setProduct(data);
        const revs = await dataService.getReviews(data.id);
        setReviews(revs);
      }
      setLoading(false);
    };
    loadProduct();
  }, [id]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <p>Loading product...</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="not-found">
        <h2>Product Not Found</h2>
        <p>The product you're looking for doesn't exist.</p>
        <Link to={ROUTES.PRODUCTS} className="btn btn-primary">
          Back to Products
        </Link>
      </div>
    );
  }

  const category = PRODUCT_CATEGORIES.find(c => c.value === product.category);
  const avgRating = reviews.length > 0
    ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length
    : product.rating;

  const handleSubmitReview = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!product || !reviewComment.trim()) return;
    setSubmittingReview(true);
    try {
      await dataService.createReview({
        productId: product.id,
        reviewer: user?.name || 'Anonymous',
        rating: reviewRating,
        comment: reviewComment.trim(),
      });
      const revs = await dataService.getReviews(product.id);
      setReviews(revs);
      setReviewComment('');
      setReviewRating(5);
      setShowReviewForm(false);
    } catch (err) {
      console.error('Failed to submit review:', err);
    } finally {
      setSubmittingReview(false);
    }
  };

  return (
    <div className="product-details-page">
      {/* Header */}
      <div className="product-header">
        <div className="product-header-image">
          <img
            src={product.imageUrl || '/images/products/placeholder.svg'}
            alt={product.name}
            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/images/products/placeholder.svg'; }}
          />
          {product.featured && <span className="featured-badge">⭐ Featured</span>}
        </div>
      </div>

      {/* Content */}
      <div className="product-detail-content">
        <div className="product-info">
          <Link to={ROUTES.PRODUCTS} className="back-link">← Back to Products</Link>
          <div className="product-meta">
            <span className="product-category-badge">
              {category?.icon} {category?.label}
            </span>
            <span className="product-pet-badge">
              <PetIcon type={product.petType} size={16} /> {product.petType}
            </span>
          </div>

          <h1>{product.name}</h1>
          <p className="product-description">{product.description}</p>

          {/* Tags */}
          {product.tags && product.tags.length > 0 && (
            <div className="product-tags">
              {product.tags.map(tag => (
                <span key={tag} className="tag-pill">{tag}</span>
              ))}
            </div>
          )}

          {/* Reviews */}
          <div className="product-reviews-section">
            <h3>Reviews ({reviews.length})</h3>
            {reviews.length > 0 ? (
              <div className="reviews-list">
                {reviews.map(review => (
                  <div key={review.id} className="review-item">
                    <div className="review-header">
                      <span className="review-author">{review.reviewer}</span>
                      <span className="review-rating">
                        {'★'.repeat(review.rating)}{'☆'.repeat(5 - review.rating)}
                      </span>
                    </div>
                    <p className="review-comment">{review.comment}</p>
                    <span className="review-date">
                      {new Date(review.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-reviews">No reviews yet. Be the first to review!</p>
            )}

            {/* Leave a Review */}
            {isAuthenticated ? (
              showReviewForm ? (
                <form className="review-form" onSubmit={handleSubmitReview}>
                  <div className="review-form-rating">
                    <label>Rating</label>
                    <div className="star-picker">
                      {[1, 2, 3, 4, 5].map(star => (
                        <button
                          key={star}
                          type="button"
                          className={`star-btn ${star <= reviewRating ? 'active' : ''}`}
                          onClick={() => setReviewRating(star)}
                        >
                          ★
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="review-form-comment">
                    <label>Your Review</label>
                    <textarea
                      value={reviewComment}
                      onChange={e => setReviewComment(e.target.value)}
                      placeholder="Share your experience with this product..."
                      rows={4}
                      required
                    />
                  </div>
                  <div className="review-form-actions">
                    <button type="submit" className="btn btn-primary" disabled={submittingReview || !reviewComment.trim()}>
                      {submittingReview ? 'Submitting...' : 'Submit Review'}
                    </button>
                    <button type="button" className="btn btn-outline" onClick={() => setShowReviewForm(false)}>
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <button className="btn btn-primary review-btn" onClick={() => setShowReviewForm(true)}>
                  ✍️ Leave a Review
                </button>
              )
            ) : (
              <button className="btn btn-primary review-btn" onClick={login}>
                Sign in to Leave a Review
              </button>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="product-sidebar">
          <div className="price-card">
            <span className="product-price">${product.price.toFixed(2)}</span>
          </div>

          <div className="stock-action-card">
            <span className={`stock-status ${product.inStock ? 'in-stock' : 'out-of-stock'}`}>
              {product.inStock ? '✓ In Stock' : '✗ Out of Stock'}
            </span>
            <AddToCartButton productId={product.id} inStock={product.inStock} />
          </div>

          <div className="rating-card">
            <span className="rating-stars">
              {'★'.repeat(Math.round(avgRating))}{'☆'.repeat(5 - Math.round(avgRating))}
            </span>
            <span className="rating-text">{avgRating.toFixed(1)} / 5.0</span>
            <span className="review-count">{reviews.length} reviews</span>
          </div>

          <div className="product-dates">
            <p>Listed: {new Date(product.createdAt).toLocaleDateString()}</p>
            <p>Updated: {new Date(product.updatedAt).toLocaleDateString()}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;
